import React, { useReducer, useContext } from "react"
import { Action } from "../models/Action";
import PolicyManagementApi from "../api/policymanagement.api";
import { INSURANCE_CONTEXT_ACTIONS } from "./InsuracePurchaseContext";
import { HullApi } from "../api/hull.api";
import policymanagementApi from "../api/policymanagement.api";
import { useParams } from "react-router-dom";
import { EventEmitterService, EventKey } from "../services/EventEmitterService";
import { awaitWithLoading } from "../services/utils";
import { PolicyManagementContextStore, ACTIONS as PolicyManagementContextActions } from './PolicyManagementContext';
import { ActiveInsuranceModel } from "../models/insurance-models/ActiveInsuranceModel";
import { PolicyRenewalContextStore } from "./PolicyRenewalContext";
import { SUBSCRIPTION_STATE } from "../models/insurance-models/PolicySubscriptionState";
import PolicyManagementRenewalApi from '../api/policmanagement.renewal.api';
import PolicyManagementImpersonationApi from '../api/policmanagement.impersonation.api';
import { SessionService } from "../services/SessionService";

export const ACTIONS = {
    EQUIPMENT_TYPES_LOADED: 'EQUIPMENT_TYPES_LOADED',
    NEW_EXTENSION_OFFER: 'NEW_EXTENSION_OFFER',
    REPLACE_DRONES: 'REPLACE_DRONES',
    REPLACE_EQUIPMENT: 'REPLACE_EQUIPMENT',
    RESET_CHANGES: 'RESET_CHANGES',
}

const defaultState = {
    dispatch: (action: Action) => {},
    addDrone: () => {},
    editDrone: () => {},
    addEquipment: () => {},
    editEquipment: () => {},
    deleteDrone: () => {},
    deleteEquipment: () => {},
    editInsuredDrone: (drone, index) => {},
    editInsuredEquipment: (equipment, index) => {},
    purchasePhysicalExtensionOffer: () => {},
    isHullChanged: () => {},
    equipmentTypes: [],
    addedDrones: [],
    addedEquipment: [],
    phyiscalExtensionOffer: {},
    additionalCost: 0,
}

const reducer = (state, action: Action) => {
    switch (action.type) {
        case ACTIONS.EQUIPMENT_TYPES_LOADED:
            return {...state, equipmentTypes: [...action.data]}
        case ACTIONS.NEW_EXTENSION_OFFER:
            return {...state, phyiscalExtensionOffer: action.data, additionalCost: action.data.finalCost}
        case ACTIONS.REPLACE_DRONES:
            return {...state, addedDrones: action.data}
        case ACTIONS.REPLACE_EQUIPMENT:
            return {...state, addedEquipment: action.data}
        case ACTIONS.RESET_CHANGES:
            return {...state, 
                                addedDrones: [],
                                addedEquipment: [],
                                phyiscalExtensionOffer: {},
                                additionalCost: 0}
      default: 
        return state
    }
};


export const PolicyHullExtensionContextStore = React.createContext(defaultState)

const PolicyHullExtensionContext = (props) => {
    const [state, dispatch] = useReducer(reducer, defaultState)
    const policyManagementContext = useContext(PolicyManagementContextStore)
    const policyRenewalContext = useContext(PolicyRenewalContextStore)
    const { insuranceId } = useParams()
    
    const loadEquipmentTypes = async () => {
        let types = await HullApi.getEquipmentTypes()
        dispatch(new Action(ACTIONS.EQUIPMENT_TYPES_LOADED, types))
    }

    const purchasePhysicalExtensionOffer = async () => {
        let res = await awaitWithLoading(policymanagementApi.applyHullExtension(insuranceId, state.phyiscalExtensionOffer.id))
        if(res.ok) {
            policyManagementContext.dispatch(new Action(PolicyManagementContextActions.UPDATE_INSURANCE,
                 { insurance: res.parsedData }))
            EventEmitterService.dispatch(EventKey.PHYSICAL_EXTENSION_PURCHASE_SUCCESS)
        }
        return res
    }

    const getPhysicalExtensionOffer = async (insuranceId, drones, equipment) => {
        let res = await awaitWithLoading(policymanagementApi.getPhysicalDamageOffer(insuranceId, drones, equipment))
        if(res.ok) {
            dispatch(new Action(ACTIONS.REPLACE_DRONES, drones))
            dispatch(new Action(ACTIONS.REPLACE_EQUIPMENT, equipment))
            dispatch(new Action(ACTIONS.NEW_EXTENSION_OFFER, res.parsedData))
            EventEmitterService.dispatch(EventKey.PHYSICAL_EXTENSION_OFFER_SUCCESS)
        }
        else {
            EventEmitterService.dispatch(EventKey.ShowError, res)
        }
    }

    const handleResponseAndRefresh = (res) => {
        if(res.ok) {
            EventEmitterService.dispatch(EventKey.REFRESH_RENEWAL_OFFERS)
            policyManagementContext.dispatch(new Action(PolicyManagementContextActions.UPDATE_INSURANCE, { insurance: res.parsedData }))
        }
        else 
            EventEmitterService.dispatch(EventKey.ShowError, res)
    }

    const addDrone = async (drone) => {
        if(policyRenewalContext.insuranceSubscriptionState == SUBSCRIPTION_STATE.RENEWAL) {
            let res = await awaitWithLoading(PolicyManagementRenewalApi.addDrone(insuranceId, drone))
            handleResponseAndRefresh(res)
            if (res.ok){
                EventEmitterService.dispatch(EventKey.ShowSuccess, {title: "The drone was successfully added to your upcoming renewal", body: "The changes will appear on your next policy."})
            }
        }
        else {
            let drones = [...state.addedDrones, drone]
            getPhysicalExtensionOffer(insuranceId, drones, state.addedEquipment)
        }
    }

    const editDrone = async (drone, index) => {
        if(policyRenewalContext.insuranceSubscriptionState == SUBSCRIPTION_STATE.RENEWAL) {
            let insurance: ActiveInsuranceModel = policyManagementContext.active_policies.find(item => item.insuranceId == insuranceId)
            let droneId = insurance.subscription.drones[index];
            drone.id = droneId.id;
            let res = await awaitWithLoading(PolicyManagementRenewalApi.editDrone(insuranceId, drone))
            handleResponseAndRefresh(res)
        }
        // else if(policyRenewalContext.insuranceSubscriptionState == SUBSCRIPTION_STATE.CURRENT && SessionService.isAdmin()) {
        //     let insurance: ActiveInsuranceModel = policyManagementContext.active_policies.find(item => item.insuranceId == insuranceId)
        //     let newDronesList = insurance.insuredDrones.map((d, i) => i == index ? drone : d)
        //     PolicyManagementImpersonationApi.modifyExistingHull(insuranceId, newDronesList, insurance.insuredEquipment)
        // }
        else {
            let drones = state.addedDrones.map((d, i) => i == index ? Object.assign(d, drone) : d)
            getPhysicalExtensionOffer(insuranceId, drones, state.addedEquipment)
        }
    }

    const addEquipment = async (equip) => {
        if(policyRenewalContext.insuranceSubscriptionState == SUBSCRIPTION_STATE.RENEWAL) {
            let res = await awaitWithLoading(PolicyManagementRenewalApi.addEquipment(insuranceId, equip))
            handleResponseAndRefresh(res)
            if (res.ok){
                EventEmitterService.dispatch(EventKey.ShowSuccess, {title: 'The ' + equip.type + " was successfully added to your upcoming renewal", body: "The changes will appear on your next policy."})
            }
        }
        else {
            let equipment = [...state.addedEquipment, equip]
            getPhysicalExtensionOffer(insuranceId, state.addedDrones, equipment)
        }
    }

    const editEquipment = async (equip, index) => {
        if(policyRenewalContext.insuranceSubscriptionState == SUBSCRIPTION_STATE.RENEWAL) {
            let insurance: ActiveInsuranceModel = policyManagementContext.active_policies.find(item => item.insuranceId == insuranceId)
            let equipment = insurance.subscription.equipment[index]
            equip.id = equipment.id;
            let res = await awaitWithLoading(PolicyManagementRenewalApi.editEquipment(insuranceId, equip))
            handleResponseAndRefresh(res)
        }
        // else if(policyRenewalContext.insuranceSubscriptionState == SUBSCRIPTION_STATE.CURRENT && SessionService.isAdmin()) {
        //     let insurance: ActiveInsuranceModel = policyManagementContext.active_policies.find(item => item.insuranceId == insuranceId)
        //     let newEList = insurance.insuredEquipment.map((e, i) => i == index ? equip : e)
        //     PolicyManagementImpersonationApi.modifyExistingHull(insuranceId, insurance.insuredDrones, newEList)
        // }
        else {
            let equipment = state.addedEquipment.map((e, i) => i == index ? Object.assign(e, equip) : e)
            getPhysicalExtensionOffer(insuranceId, state.addedDrones, equipment)
        }
    }

    const deleteDroneInEditMode = async (index, drone?) => {
        let drones = state.addedDrones.filter((d, i) => i != index)
            getPhysicalExtensionOffer(insuranceId, drones, state.addedEquipment)
    }

    const deleteDrone = async (index, drone?) => {
        if(policyRenewalContext.insuranceSubscriptionState == SUBSCRIPTION_STATE.RENEWAL) {
            let res = await awaitWithLoading(PolicyManagementRenewalApi.deleteDrone(insuranceId, drone.id))
            handleResponseAndRefresh(res)
            if (res.ok){
                EventEmitterService.dispatch(EventKey.ShowSuccess, {title: "The drone was successfully removed from your upcoming renewal", body: "The changes will appear on your next policy."})
            }
        }
        else if(policyRenewalContext.insuranceSubscriptionState == SUBSCRIPTION_STATE.CURRENT && SessionService.isAdmin()) {
            let res = await awaitWithLoading(PolicyManagementImpersonationApi.deleteDroneOrEquipmentFromHull(insuranceId, drone, undefined))
            handleResponseAndRefresh(res)
            if (res.ok){
                EventEmitterService.dispatch(EventKey.ShowSuccess, {title: "The drone was successfully removed from your policy", body: "The changes will appear on your policy."})
            }
        }
    }

    const deleteEquipmentInEditMode = async (index, equipment?) => {
        let equipments = state.addedEquipment.filter((e,i) => index != i)
            getPhysicalExtensionOffer(insuranceId, state.addedDrones, equipments)
    }

    const deleteEquipment = async (index, equipment?) => {
        if(policyRenewalContext.insuranceSubscriptionState == SUBSCRIPTION_STATE.RENEWAL) {
            let res = await awaitWithLoading(PolicyManagementRenewalApi.deleteEquipment(insuranceId, equipment.id))
            handleResponseAndRefresh(res)
            if (res.ok){
                EventEmitterService.dispatch(EventKey.ShowSuccess, {title: 'The ' + equipment.type.toLowerCase() + " was successfully removed from your upcoming renewal", body: "The changes will appear on your next policy."})
            }
        }
        else if(policyRenewalContext.insuranceSubscriptionState == SUBSCRIPTION_STATE.CURRENT && SessionService.isAdmin()) {
            let res = await awaitWithLoading(PolicyManagementImpersonationApi.deleteDroneOrEquipmentFromHull(insuranceId, undefined, equipment))
            handleResponseAndRefresh(res)
            if (res.ok){
                EventEmitterService.dispatch(EventKey.ShowSuccess, {title: "The equipment was successfully removed from your policy", body: "The changes will appear on your policy."})
            }
        }
    }

    const editInsuredDrone = async (drone, index) => {
        let insurance: ActiveInsuranceModel = policyManagementContext.active_policies.find(item => item.insuranceId == insuranceId) || policyManagementContext.scheduled_policies.find(item => item.insuranceId == insuranceId);
        let drones = insurance.insuredDrones.map((d, i) => i == index ? Object.assign(d, drone) : d)
        let res = await awaitWithLoading(policymanagementApi.modifyExistingHull(insuranceId, drones, insurance.insuredEquipment))
        policyManagementContext.dispatch(new Action(PolicyManagementContextActions.UPDATE_INSURANCE,
            { insurance: res.parsedData }))
        EventEmitterService.dispatch(EventKey.INSURED_HULL_EDIT_SUCCESS)
    }

    const editInsuredEquipment = async (equip, index) => {
        let insurance: ActiveInsuranceModel = policyManagementContext.active_policies.find(item => item.insuranceId == insuranceId) || policyManagementContext.scheduled_policies.find(item => item.insuranceId == insuranceId);
        let equipment = insurance.insuredEquipment.map((e, i) => i == index ? Object.assign(e, equip) : e)
        let res = await awaitWithLoading(policymanagementApi.modifyExistingHull(insuranceId, insurance.insuredDrones, equipment))
        policyManagementContext.dispatch(new Action(PolicyManagementContextActions.UPDATE_INSURANCE,
            { insurance: res.parsedData }))
        EventEmitterService.dispatch(EventKey.INSURED_HULL_EDIT_SUCCESS)
    }

    const isHullChanged = () => {
        return ((state.addedDrones && state.addedDrones.length > 0) || (state.addedEquipment && state.addedEquipment.length > 0));
    }

    React.useEffect(() => {
        loadEquipmentTypes()
    }, [])

    return (<PolicyHullExtensionContextStore.Provider value={{ ...state, addDrone, addEquipment,
             editEquipment, editDrone, purchasePhysicalExtensionOffer,
             editInsuredDrone, editInsuredEquipment, isHullChanged,
              deleteEquipment, deleteDrone, dispatch,deleteDroneInEditMode, deleteEquipmentInEditMode }}>
            {props.children}
        </PolicyHullExtensionContextStore.Provider>)   
}

export default PolicyHullExtensionContext



